import React from 'react';
import { Link } from 'react-router-dom';

interface LogoProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  asLink?: boolean;
}

const Logo: React.FC<LogoProps> = ({ size = 'md', className = '', asLink = true }) => {
  const sizeConfig = {
    sm: { icon: 44, iconRadius: 12, fontSize: '1.1rem', mySize: '1.3rem', gap: 16, px: 20, py: 12, br: 24 },
    md: { icon: 52, iconRadius: 14, fontSize: '1.3rem', mySize: '1.55rem', gap: 20, px: 26, py: 14, br: 30 },
    lg: { icon: 64, iconRadius: 18, fontSize: '1.6rem', mySize: '1.9rem', gap: 24, px: 30, py: 18, br: 34 },
  };

  const s = sizeConfig[size];

  const inner = (
    <div
      className={`inline-flex items-center cursor-pointer select-none group transition-all duration-300 hover:-translate-y-0.5 ${className}`}
      style={{
        background: '#E0E5EC',
        borderRadius: s.br,
        padding: `${s.py}px ${s.px}px`,
        boxShadow: '9px 9px 16px rgb(163,177,198,0.6), -9px -9px 16px rgba(255,255,255,0.5)',
        gap: s.gap,
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLDivElement).style.boxShadow =
          '12px 12px 20px rgb(163,177,198,0.7), -12px -12px 20px rgba(255,255,255,0.6)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLDivElement).style.boxShadow =
          '9px 9px 16px rgb(163,177,198,0.6), -9px -9px 16px rgba(255,255,255,0.5)';
      }}
    >
      {/* Icon mark — deep inset well */}
      <div
        style={{
          width: s.icon,
          height: s.icon,
          borderRadius: s.iconRadius,
          background: '#E0E5EC',
          boxShadow: 'inset 8px 8px 16px rgb(163,177,198,0.7), inset -8px -8px 16px rgba(255,255,255,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {/* CPU / neural node SVG icon */}
        <svg
          width={s.icon * 0.5}
          height={s.icon * 0.5}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#6C63FF" />
              <stop offset="100%" stopColor="#8B84FF" />
            </linearGradient>
          </defs>
          {/* CPU chip icon */}
          <rect x="7" y="7" width="10" height="10" rx="2" stroke="url(#logoGrad)" strokeWidth="1.5" fill="none" />
          <rect x="9" y="9" width="6" height="6" rx="1" fill="url(#logoGrad)" />
          {/* Pins top */}
          <line x1="9" y1="7" x2="9" y2="4" stroke="url(#logoGrad)" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="12" y1="7" x2="12" y2="4" stroke="url(#logoGrad)" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="15" y1="7" x2="15" y2="4" stroke="url(#logoGrad)" strokeWidth="1.5" strokeLinecap="round" />
          {/* Pins bottom */}
          <line x1="9" y1="17" x2="9" y2="20" stroke="url(#logoGrad)" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="12" y1="17" x2="12" y2="20" stroke="url(#logoGrad)" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="15" y1="17" x2="15" y2="20" stroke="url(#logoGrad)" strokeWidth="1.5" strokeLinecap="round" />
          {/* Pins left */}
          <line x1="7" y1="9" x2="4" y2="9" stroke="url(#logoGrad)" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="7" y1="12" x2="4" y2="12" stroke="url(#logoGrad)" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="7" y1="15" x2="4" y2="15" stroke="url(#logoGrad)" strokeWidth="1.5" strokeLinecap="round" />
          {/* Pins right */}
          <line x1="17" y1="9" x2="20" y2="9" stroke="url(#logoGrad)" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="17" y1="12" x2="20" y2="12" stroke="url(#logoGrad)" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="17" y1="15" x2="20" y2="15" stroke="url(#logoGrad)" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>

      {/* Wordmark */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
          fontWeight: 800,
          letterSpacing: '-0.02em',
          lineHeight: 1,
        }}
      >
        <span style={{ color: '#3D4852', fontSize: s.fontSize }}>CODE</span>
        <span
          style={{
            fontSize: s.mySize,
            fontWeight: 800,
            background: 'linear-gradient(135deg, #6C63FF 0%, #8B84FF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            padding: '0 6px',
            transition: 'transform 300ms ease-out',
          }}
        >
          MY
        </span>
        <span style={{ color: '#3D4852', fontSize: s.fontSize }}>AI</span>
      </div>
    </div>
  );

  if (!asLink) return inner;

  return (
    <Link to="/" className="inline-flex" style={{ textDecoration: 'none' }}>
      {inner}
    </Link>
  );
};

export default Logo;
